
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpEvent } from '@angular/common/http';

import { Observable } from 'rxjs/internal/Observable';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';

const WORKER_PATH = './assets/js/pdf.worker.min.mjs'; // Simple string path that matches the angular.json output. Needed for ivy.

@Injectable({ providedIn: 'root' })
export class FileService {
    private _baseURL = '/api/files/';
    private headerOptions = {headers: new HttpHeaders({'Content-Type': 'application/json'})};

    constructor(private httpClient: HttpClient) { }

    postFile(fileToUpload: File): Observable<boolean> {
        const formData: FormData = new FormData();
        formData.append('fileKey', fileToUpload, fileToUpload.name);

        return this.httpClient.post<boolean>(this._baseURL + 'upload', formData, this.headerOptions);
    }


    public removeSpecialCharsFromFilename(filename) {
        // get extension so we can add it after stripping non-alpha-numeric chars
        const fileExt = filename.split('.').pop();
        // return new filename. Add extension back to it
        const dateStampDate = new Date();
        const dateStamp = (dateStampDate.getMonth() + 1).toString() + dateStampDate.getDate().toString() + dateStampDate.getFullYear().toString() + 
            dateStampDate.getMinutes().toString() + dateStampDate.getSeconds().toString();

        return filename.replace(/\W/g, '') + dateStamp + '_.' + fileExt;
    }


    uploadSingle(file: any): Observable<HttpEvent<any>> {
        const formDataObject: FormData = null;
        const formData: FormData = new FormData();
        formData.append('file', file, file.name);

        return this.httpClient.post<any>(this._baseURL + 'upload', formData, {reportProgress: true, observe: 'events'});
    }


    public uploadAsIsToYeehro(_filename: string, file: File): Observable<HttpEvent<any>> {
        const formData: FormData = new FormData();
        formData.append('file', file, _filename);

        return this.httpClient.post<any>('https://www.yeehro.com/api/files/eduhealthcare-resume', formData, {reportProgress: true, observe: 'events'});


        // this.httpClient.post<any>('https://www.yeehro.com/api/files/upload-as-is/resumes', formData, {reportProgress: true, observe: 'events'}

        // const formDataObjects: FormData[] = [];

        // files.forEach(file => {
        //     const formData: FormData = new FormData();
        //     formData.append('file', file, file.name);
        //     formDataObjects.push(formData);
        // });

        // const httpRequests = formDataObjects.map((formData) => 
        //     this.httpClient.post<any>('https://www.yeehro.com/api/files/upload-as-is/resumes', formData, {reportProgress: true, observe: 'events'}
        // ));

        // return forkJoin(httpRequests);
    }


    public upload(files: Set<File>): Observable<HttpEvent<any>[]> {
        // this will be the our resulting map

        const formDataObjects: FormData[] = [];

        files.forEach(file => {
            const formData: FormData = new FormData();
            formData.append('file', file, file.name);
            formDataObjects.push(formData);
        });


        const httpRequests = formDataObjects.map((formData) => 
            this.httpClient.post<any>(this._baseURL + 'upload', formData, {reportProgress: true, observe: 'events'})
        );

        return forkJoin(httpRequests);
    }


    public uploadToSpecifedFolder(files: Set<File>, _filePath): Observable<HttpEvent<any>[]> {
        const formDataObjects: FormData[] = [];

        files.forEach(file => {
            const formData: FormData = new FormData();
            formData.append('file', file, file.name);
            formDataObjects.push(formData);
        });

        const httpRequests = formDataObjects.map((formData) => 
            this.httpClient.post<any>(this._baseURL + 'upload/' + _filePath, formData, {reportProgress: true, observe: 'events'})
        );

        return forkJoin(httpRequests);
    }


    public buildExportForSpreadsheet(_moduleFields, _tableData) {
        // Use passed fields and table data to create a spreadsheet
        return new Promise(async (resolve) => {
            const exportData = {
                columns: _moduleFields.map(_field => { return { field: _field.fieldName, label: _field.label } }),
                data: _tableData.map(_rowInfo => {
                    let recordDetails = {};

                    for (const [key, value] of Object.entries(_rowInfo)) {
                        const fieldDef = _moduleFields.find(_mf => _mf.fieldName === key);

                        if ((fieldDef && fieldDef !== undefined)) {
                            if (fieldDef['type']['inputType'] === 'Date') {
                                const d = new Date(value as any);
                                recordDetails[key] = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
                            }
                            else if (fieldDef['type']['inputType'] === 'Relate') {
                                recordDetails[key] = (value && value !== undefined) ? (value['_id'] && value['_id'] !== undefined) ? value['_id'] : value : null
                            } else if (fieldDef['type']['inputType'] === 'MultiRelate') {
                                const multiArray = Object.assign([], value);

                                if (multiArray && multiArray.length > 0) {
                                    let idString = '';

                                    multiArray.forEach(_item => {
                                        const idToUse = (_item && _item !== undefined) ? (_item['_id'] && _item['_id'] !== undefined) ? _item['_id'] : _item : null;
                                        if (idToUse && idToUse !== undefined) {
                                            if (idString.length) idString += ', ';
                                            idString += idToUse;
                                        }
                                    });
                                    recordDetails[key] = idString;
                                }
                            } else recordDetails[key] = value;
                        }
                    }

                    return recordDetails;
                })
            };
            resolve(exportData);
        });
    }


    exportToSpreadsheet(dataToExport): Observable<any> {
        return this.httpClient.post(this._baseURL + 'spreadsheet/export-to-spreadsheet', dataToExport, this.headerOptions);
    }


    public readFileAsDataUrl(_file: File): Promise<string> {
        return new Promise((resolve) => {
            if (!_file) return resolve('');
            const reader = new FileReader();
            reader.onload = (e: any) => resolve(e.target.result);
            reader.onerror = (_err) => {
                console.log('Error reading file as Data URL:', _err);
                resolve('');
            };
            reader.readAsDataURL(_file);
        });
    }


    public stripFileExtension(filename: string): string {
        return filename.replace(/\.[^/.]+$/, ''); // removes last dot + extension
    }


    public async generatePdfImageArray(file: File, scale = 1.5): Promise<File[]> {
        const imageFiles: File[] = [];
        if (!file) return imageFiles;

        // Dynamic import keeps pdfjs-dist (~400 KB) out of the main bundle.
        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_PATH;

        let pdf: any = null;
        try {
            const pdfData = await file.arrayBuffer();

            // Add verbosity and disableFontFace to kill the "TT" errors.
            pdf = await pdfjsLib.getDocument({
                data: pdfData,
                verbosity: 0,
                disableFontFace: true
            }).promise;

            for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                const page = await pdf.getPage(pageNum);
                const viewport = page.getViewport({ scale });
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d')!;

                canvas.width = viewport.width;
                canvas.height = viewport.height;

                await page.render({ canvas, canvasContext: ctx, viewport }).promise;

                const blob = await new Promise<Blob>(res => canvas.toBlob(b => res(b!), 'image/png'));
                const baseName = this.stripFileExtension(file?.name);
                imageFiles.push(new File([blob], `${baseName}-page-${pageNum}.png`, { type: 'image/png' }));

                // Clean up the page objects and canvas.
                page.cleanup();
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                canvas.width = 0;
                canvas.height = 0;
            }
        } catch (_err) {
            console.log('Error generating PDF preview images:', _err);
        } finally {
            if (pdf) await pdf.destroy(); // Critical: Destroy the document to prevent errors when component is destroyed.
        }

        return imageFiles;
    }


    public async generateVideoThumbnail(_file: File, _seekTo = 0): Promise<File | null> {
        if (!_file) {
            console.log('No file provided for thumbnail generation.');
            return null;
        }

        // Check if the file is a video
        if (!_file?.type.startsWith('video/')) {
            console.log('File is not a video:', _file?.name);
            return null;
        }

        try {
            return await new Promise<File | null>((resolve) => {
                const url = URL.createObjectURL(_file);
                const video = document.createElement('video');
                video.src = url;
                video.crossOrigin = 'anonymous'; // just in case

                video.addEventListener('loadeddata', () => {
                    video.currentTime = _seekTo;
                });

                video.addEventListener('seeked', async () => {
                    try {
                        const canvas = document.createElement('canvas');
                        canvas.width = video.videoWidth;
                        canvas.height = video.videoHeight;

                        const ctx = canvas.getContext('2d')!;
                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                        const blob: Blob = await new Promise(res =>
                            canvas.toBlob(b => res(b!), 'image/png')
                        );

                        const baseName = this.stripFileExtension(_file?.name);
                        const thumbnailFile = new File([blob], `${baseName}-thumbnail.png`, { type: 'image/png' });

                        // cleanup
                        URL.revokeObjectURL(url);
                        canvas.width = 0;
                        canvas.height = 0;

                        resolve(thumbnailFile);
                    } catch (_err) {
                        console.log('Error creating thumbnail from canvas:', _err);
                        resolve(null);
                    }
                });

                video.addEventListener('error', (_err) => {
                    console.log('Error generating video thumbnail:', _err);
                    URL.revokeObjectURL(url);
                    resolve(null);
                });
            });
        } catch (_err) {
            console.log('Unexpected error generating video thumbnail:', _err);
            return null;
        }
    }
}
